/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Period } from '@src/app/model/period';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { MessageService } from '@src/app/shared/message.service';
import { Observable, of } from 'rxjs';

import { Cycle } from '@src/app/model/cycle';
import { catchError, map, tap } from 'rxjs/operators';
import { ActionColor, IFormAction } from '@src/app/shared/form/form.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { DateHelper } from '@src/app/shared/dateHelper';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { Router } from '@angular/router';
import { MyInjector } from '@src/app/app.module';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { User } from '@src/app/model/user';
import { CyclePageNavRoute } from '@src/app/routes/app-router';
import { SchedulePageNavRoute } from '@src/app/routes/schedule-page-nav';

class ProposeBudgetAction implements IFormAction {
    name = $localize`Propose`;
    color: ActionColor = 'primary';
    show = false;
    icon = 'how_to_vote';
    approvalNeeded = true;
    approvalText = $localize`Lock this budget whilst it is proposed to the members for approval`;
    cycleSvc: CycleService;

    action(cycle: Cycle, config: FormConfig) {
        return (config.service as CycleService).propose(cycle);
    }
    setup(cycle: Cycle) {
        this.show = cycle.statusId === Cycle.statusIds.EDITTING;
    }
}

class MarkBilledAction implements IFormAction {
    name = $localize`Mark Billed`;
    color: ActionColor = 'primary';
    show = false;
    icon = 'done_all';
    approvalNeeded = true;
    approvalText = $localize`Mark this budget billed - No billing will actually occur. Only take this action if billing has already been completed`;
    cycleSvc: CycleService;

    action(cycle: Cycle, config: FormConfig) {
        return (config.service as CycleService).markBilled(cycle);
    }
    setup(cycle: Cycle) {
        this.show = cycle.statusId === Cycle.statusIds.EDITTING;
    }
}
class CopyPreviousAction implements IFormAction {
    name = $localize`Copy Previous`;
    color: ActionColor = 'accent';
    show = false;
    icon = 'content_copy';
    approvalNeeded = true;
    approvalText = $localize`Copy budget figures from last year to this year?`;

    action(cycle: Cycle, config: FormConfig) {
        return (config.service as CycleService).copyPrevious(cycle);
    }
    setup(cycle: Cycle) {
        this.show = cycle.statusId === Cycle.statusIds.EDITTING;
    }
}

class CopyPreviousActualsAction implements IFormAction {
    name = $localize`Copy Actuals`;
    color: ActionColor = 'accent';
    show = false;
    icon = 'file_copy';
    approvalNeeded = true;
    approvalText = $localize`Copy last years actuals to this year? (make sure last years records complete if you do this!)`;

    action(cycle: Cycle, config: FormConfig) {
        return (config.service as CycleService).copyPreviousActual(cycle);
    }
    setup(cycle: Cycle) {
        this.show = cycle.statusId === Cycle.statusIds.EDITTING;
    }
}

class ApproveBudgetAction implements IFormAction {
    name = $localize`Approve`;
    color: ActionColor = 'primary';
    show = false;
    icon = 'approval';
    approvalNeeded = true;
    approvalText = $localize`Approve this budget, Ready to be billed`;
    action(cycle: Cycle, config: FormConfig) {
        return (config.service as CycleService).approve(cycle);
    }
    setup(cycle: Cycle) {
        this.show = cycle.statusId === Cycle.statusIds.PROPOSED;
    }
}
class RejectBudgetAction implements IFormAction {
    name = $localize`Reject`;
    color: ActionColor = 'warn';
    show = false;
    icon = 'settings_backup_restore';
    approvalNeeded = true;
    approvalText = $localize`Reject this budget and allow it to be editted again?`;
    action(cycle: Cycle, config: FormConfig) {
        return (config.service as CycleService).reject(cycle);
    }
    setup(cycle: Cycle) {
        this.show = cycle.statusId === Cycle.statusIds.PROPOSED;
    }
}

class ReviseBudgetAction implements IFormAction {
    name = $localize`Revise`;
    color: ActionColor = 'accent';
    show = false;
    icon = 'settings_backup_restore';
    approvalNeeded = true;
    approvalText = $localize`Revise this budget so that you can issue a supplementary charge?`;
    action(cycle: Cycle, config: FormConfig) {
        return (config.service as CycleService).revise(cycle);
    }
    setup(cycle: Cycle) {
        this.show = cycle.statusId === Cycle.statusIds.BILLED;
    }
}
class RestartBudgetAction implements IFormAction {
    name = $localize`Restart`;
    color: ActionColor = 'accent';
    show = false;
    icon = 'settings_backup_restore';
    approvalNeeded = true;
    approvalText = $localize`Put this budget back into editting again?`;
    action(cycle: Cycle, config: FormConfig) {
        return (config.service as CycleService).restart(cycle);
    }
    setup(cycle: Cycle) {
        this.show = cycle.statusId === Cycle.statusIds.ACCEPTED;
    }
}

class ViewReportAction implements IFormAction {
    name = $localize`Report`;
    color: ActionColor = 'primary';
    show = true;
    icon = 'summarize';
    approvalNeeded = false;
    approvalText = '';
    action(cycle: Cycle, config: FormConfig) {
        (config.service as CycleService).showReport(cycle);
        return of(null) as unknown as Observable<Cycle>;
    }
    setup(_cycle: Cycle) { this.show = true; }
}

class DownloadReportAction implements IFormAction {
    name = $localize`Download Report`;
    color: ActionColor = 'primary';
    show = true;
    icon = 'picture_as_pdf';
    approvalNeeded = false;
    approvalText = '';
    action(cycle: Cycle, config: FormConfig) {
        (config.service as CycleService).downloadReport(cycle);
        return of(null) as unknown as Observable<Cycle>;
    }
    setup(_cycle: Cycle) { this.show = true; }
}
@Injectable({
    providedIn: 'root'
})
export class CycleService extends AbstractHttpService {
    protected http = inject(HttpClient);
    protected messageService = inject(MessageService);
    protected cds = inject(ConfirmDialogService);
    protected router = inject(Router);


    protected get baseUrl() {
        return this.getAjaxPath('budgets/cycles');
    }
    protected cache: Cycle[];
    protected typeString = 'Cycles';
    protected currentCycle: Cycle;
    protected currentPeriod: Period;

    getCurrentCycle(cycles: Cycle[]) {
        for (const c of cycles) {
            if (DateHelper.isoDateIn(new Date(), c.from, c.to)) {
                return c;
            }
        }
        console.warn(cycles);
        if (cycles.length > 0) {
            this.cds.open($localize`Current cycle is not defined`,
                $localize`There is no current cycle defined, system will behave unpredictably until fixed. Do you want to fix now?`,
                () => { this.router.navigate([CyclePageNavRoute.getListRouteName()]) });
        } else {
            this.cds.alert($localize `Error`, $localize `Cycles not loaded cannot continue`);
        }
        return null;
    }

    getCurrentPeriod(cycle: Cycle) {
        for (const p of cycle.periods) {
            if (DateHelper.isoDateIn(new Date(), p.from, p.to)) {
                return p;
            }
        }
        this.cds.open($localize`Current period is not defined`,
            $localize`There is no current period defined, system will behave unpredictably until fixed. Do you want to fix now?`,
            () => { this.router.navigate([CyclePageNavRoute.getListRouteName()]) });          
    }

    enhanceList(cycles: Cycle[]) {
        for (const c of cycles) {
            if (DateHelper.isoDateIn(new Date(), c.from, c.to)) {
                this.currentCycle = c;
                for (const p of c.periods) {
                    if (DateHelper.isoDateIn(new Date(), p.from, p.to)) {
                        this.currentPeriod = p;
                        break;
                    }
                }
                break;
            }
        }
        if (!this.currentPeriod || !this.currentCycle) {
            this.cds.open($localize`Period Missing`,
                $localize`There is no current period or cycle defined, system may behave unpredictably. Do you want to fix now?`,
                () => { this.router.navigate([CyclePageNavRoute.getListRouteName()]) });
        }
        return cycles;
    }


    copyPrevious(item: Cycle): Observable<Cycle> {
        return this.statusChange(item, 'copyPrevious');
    }

    copyPreviousActual(item: Cycle): Observable<Cycle> {
        return this.statusChange(item, 'copyPreviousActual');
    }

    propose(item: Cycle): Observable<Cycle> {
        return this.statusChange(item, 'propose');
    }

    markBilled(item: Cycle): Observable<Cycle> {
        return this.statusChange(item, 'markBilled');
    }    

    approve(item: Cycle): Observable<Cycle> {
        return this.statusChange(item, 'approve');
    }

    reject(item: Cycle): Observable<Cycle> {
        return this.statusChange(item, 'reject');
    }

    billout(item: Cycle): Observable<Cycle> {
        return this.statusChange(item, 'billout');
    }

    restart(item: Cycle): Observable<Cycle> {
        return this.statusChange(item, 'restart');
    }

    revise(item: Cycle): Observable<Cycle> {
        return this.statusChange(item, 'revise');
    }

    showReport(cycle: Cycle): void {
        const url = `${this.getBaseUrl()}/${cycle.id}/report`;
        this.http.get(url, { headers: { Accept: 'text/html' }, responseType: 'text' }).pipe(
            catchError(this.handleError<string>('budgetReport'))
        ).subscribe(html => {
            if (!html) { return; }
            const w = Math.round(screen.availWidth * 0.85);
            const h = Math.round(screen.availHeight * 0.85);
            const top  = window.screenTop  + Math.round(screen.availHeight * 0.075);
            const left = window.screenLeft + Math.round(screen.availWidth  * 0.075);
            const opts = `toolbar=no,location=no,menubar=no,scrollbars=yes,resizable=yes,` +
                         `width=${w},height=${h},top=${top},left=${left}`;
            const win = window.open('', `Budget Report — ${cycle.name}`, opts);
            win.document.write(html);
            win.document.close();
        });
    }

    downloadReport(cycle: Cycle): void {
        const url = `${this.getBaseUrl()}/${cycle.id}/reportPdf`;
        this.downloadFile(url, null, 'application/pdf');
    }

    getActions(): IFormAction[] {
        const cus = MyInjector.instance.get(CurrentUserService);
        const priv = User.privilege(cus.currentUser, 'Cycle');
        const actions: IFormAction[] = [];
        if (priv?.put) {
            actions.push(
                new ViewReportAction(),
                new DownloadReportAction(),
                new CopyPreviousAction(),
                new CopyPreviousActualsAction(),
                new ProposeBudgetAction(),
                new RejectBudgetAction(),
                new ApproveBudgetAction(),
                new RestartBudgetAction(),
                new ReviseBudgetAction(),
                new MarkBilledAction()
            );
        }
        return actions;
    }

    statusChange(item: Cycle, change: string): Observable<Cycle> {
        const url = `${this.baseUrl}/${item.id}/${item.revision}/${change}`;
        return this.http.put<Cycle>(url, null, this.httpOptions)
            .pipe(
                map((data: Cycle) => data),
                tap(() => {
                    this.messageService.show(this.msgUpdated);
                }),
                catchError((error: HttpErrorResponse) => {
                    // Proposing is refused while any schedule is stale - members would be shown a
                    // share we know to be wrong. Only propose raises it, but the guard is cheap.
                    if (error.error?.code === 'PORTIONS_OUT_OF_DATE') {
                        return this.cds.open($localize `Review Schedule Portions`,
                            error.error.message + $localize ` Go to the schedules now?`,
                            () => this.router.navigate([SchedulePageNavRoute.getListRouteName()]));
                    }
                    return this.handleError<Cycle>(change + this.typeString)(error);
                })
            );
    }

    createNext(item: Cycle): Observable<Cycle> {
        const url = `${this.baseUrl}/${item.id}/${item.revision}/createNext`;
        return this.http.post<Cycle>(url, null, this.httpOptions)
            .pipe(
                map((data: Cycle) => data),
                tap(() => {
                    this.messageService.show('Next cycle created');
                }),
                catchError(this.handleError<Cycle>(this.typeString))
            );
    }

    yearEnd(item: Cycle): Observable<Cycle> {
        const url = `${this.baseUrl}/${item.id}/${item.revision}/yearEnd`;
        return this.http.post<Cycle>(url, null, this.httpOptions)
            .pipe(
                map((data: Cycle) => data),
                tap(() => {
                    this.messageService.show('Year End Done...');
                }),
                catchError(this.handleError<Cycle>(this.typeString))
            );
    }
}
